import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useDispatch, useSelector } from 'react-redux';
import "./ManualExpenseTable.css";
import ManualExpenseFilterCard from '../ManualExpenseFilter/ManualExpenseFilterCard';
import { selectUserData, selectUserToken } from '../../../../../features/userSlice';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { setPage, setRowsPerPage, setSingleExpenseData, setSort } from '../../../../../features/manualExpenseSlice';
import { CheckPermissions } from '../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions';
import { Avatar, Pagination, Stack, TablePagination, TableSortLabel, Typography } from '@mui/material';
import React from 'react';
import { ManualExpenseEndPointLogic } from '../../../../../logic/Finance/ProviderExpense/ManualExpenseEndpointLogic';
import { formatDate } from '../../../../../utils/helpers';

const ManualExpenseTable = () => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const filteredManualExpense = useSelector((state: any) => state.manualExpense.filteredManualExpense);
  const { selectedAsset, selectedSource, selectedDate, selectedManualExpense, allTrafficSources, page, rowsPerPage, sort } = useSelector((state: any) => state?.manualExpense);
  const userData = useSelector(selectUserData);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);

  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));
    // Update the URL param when page changes
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  const formatDateToDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0"); // Get day and pad with leading zero
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Get month (0-11) and pad
    const year = date.getFullYear(); // Get year
    return `${day}/${month}/${year}`; // Return in DD/MM/YYYY format
  };

  const formatNumberWithCommas = (value: any) => {
    if (value === null || value === undefined || value === "") {
      return "0";
    }
    const number = Number(value);
    if (isNaN(number)) {
      return value;
    }
    return number.toLocaleString();
  };

  const siteNames = userData?.website_permissions?.map((permission: any) => {
    const key = Object.keys(permission)[0];
    return {
      site: permission[key],
      key: key,
    };
  });

  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }
      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }
        let aValue = a[keytoSort];
        let bValue = b[keytoSort];

        if (keytoSort === "related_asset") {
          aValue = siteNames.find((source: any) => source.key === a.website_shared_uid)?.site?.ui_site_name || "";
          bValue = siteNames.find((source: any) => source.key === b.website_shared_uid)?.site?.ui_site_name || "";
        } else if (keytoSort === "source") {
          aValue =
            allTrafficSources.find(
              (source: any) => source.shared_uid === a.traffic_source__shared_uid
            )?.traffic_source_name || "";
          bValue =
            allTrafficSources.find(
              (source: any) => source.shared_uid === b.traffic_source__shared_uid
            )?.traffic_source_name || "";
        } else {
          aValue = a[keytoSort];
          bValue = b[keytoSort];
        }
        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        if (aValue === "") return 1;
        if (bValue === "") return -1;
        // Special case: if sorting by date fields, compare as Date objects
        if (keytoSort === "modified" || keytoSort === "created" || keytoSort === "activity_date") {
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          return sort.direction === "asc" ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
        }
        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc" ? String(aValue).localeCompare(String(bValue)) : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sort]
  );

  const filteredData = useMemo(() => {
    const filtered = filteredManualExpense.filter((expense: any) => {
      const statusAssets = selectedAsset && selectedAsset?.length !== 0 ? selectedAsset.includes(expense?.website_name) : true;
      const statusSource = selectedSource && selectedSource.length !== 0 ? selectedSource.includes(expense?.traffic_source__shared_uid) : true;
      const expenseName = selectedManualExpense && selectedManualExpense.length !== 0 ? selectedManualExpense.includes(expense?.expense_name) : true;
      const isDateInRange =
        selectedDate?.length === 2 && selectedDate[0] && selectedDate[1]
          ? selectedDate[0] === selectedDate[1] // If both dates are the same, check for equality
            ? new Date(expense?.date).toDateString() ===
            new Date(selectedDate[0]).toDateString()
            : new Date(expense?.date) >= new Date(selectedDate[0]) &&
            new Date(expense?.date) <= new Date(new Date(selectedDate[1]).setHours(23, 59, 59, 999))
          : true;
      return (statusAssets && statusSource && expenseName && isDateInRange);
    });
    return getSortedData(filtered);
  }, [filteredManualExpense, selectedAsset, selectedSource, selectedManualExpense, selectedDate, getSortedData]);

  const tableheaders = [
    { id: 1, KEY: "expense_name", LABEL: "Expense Name", sortable: true },
    { id: 2, KEY: "website_name", LABEL: "Related Asset", sortable: true },
    { id: 3, KEY: "source", LABEL: "Source", sortable: true },
    { id: 4, KEY: "date", LABEL: "Date", sortable: true },
    { id: 5, KEY: "clicks", LABEL: "Clicks", sortable: true },
    { id: 6, KEY: "impressions", LABEL: "Impressions", sortable: true },
    { id: 7, KEY: "expenses", LABEL: "Expenses", sortable: true },
    { id: 8, KEY: "created", LABEL: "Created on", sortable: true },
    { id: 9, KEY: "created_by", LABEL: "Created by", sortable: true },
  ];

  // function to handle click on table headers
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(setSort({ keytoSort: header.KEY, direction: header.KEY === sort.keytoSort && sort.direction === "asc" ? "desc" : "asc", })
    );
  };

  useEffect(() => {
    // Get current page from URL searchParams if it exists
    const currentPage = searchParams.get("page");

    // If page exists in URL, use that value (convert to number)
    if (currentPage !== null) {
      dispatch(setPage(Number(currentPage)));
    }
    // Otherwise reset to page 0 (when filters change)
    else {
      dispatch(setPage(0));
      // Also update URL to reflect page 0
      setSearchParams((prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);
        updatedParams.set("page", "0");
        return updatedParams;
      });
    }
    // eslint-disable-next-line
  }, [searchParams, selectedAsset, selectedSource, selectedDate, selectedManualExpense]);

  useEffect(() => {
    // Only show loading when data is actually being fetched
    // Once filteredManualExpense is populated (even if empty), we're done loading
    if (filteredManualExpense !== undefined && filteredManualExpense !== null) {
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, [filteredManualExpense]);

  const search = searchParams.get("search");
  const hasEditManualExpansesPermission = CheckPermissions(
    userData?.data?.products?.finance,
    "providers_expenses",
    "edit_manual_expenses"
  );

  return (
    <>
      <ManualExpenseFilterCard />
      <TableContainer className="themeWrap" component={Paper}>
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header) => (
                <TableCell key={header.id}>
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.LABEL}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredData?.length > 0 ? (
              <>
                {filteredData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <React.Fragment key={index}>
                    <TableRow key={index} sx={{ "& > *": { borderBottom: "1px solid rgba(224, 224, 224, 1) !important", }, }}>
                      <TableCell align="left">
                        {(() => {
                          const params = new URLSearchParams(searchParams);
                          params.set("expenseShareUid", row?.shared_uid || "");
                          params.set("expenseName", row?.expense_name || "");
                          params.set("websiteName", row?.website_name || "");
                          params.set("websiteSharedUid", row?.website_shared_uid || "");
                          params.set("currencyShortCode", row?.currency__currency_code || "");
                          params.set("trafficSourceUid", row?.traffic_source__shared_uid || "");
                          params.set("date", row?.date || "");
                          params.set("clicks", row?.clicks?.toString() || "");
                          params.set("impressions", row?.impressions?.toString() || "");
                          params.set("expenses", row?.expenses?.toString() || "");
                          params.set("created", row?.created || "");
                          params.set("createdBy", row?.created_by || "");
                          if (search) {
                            params.set("search", search);
                          }
                          const queryString = params.toString();
                          const handleLinkClick = () => {
                            dispatch(setSingleExpenseData(row))
                            ManualExpenseEndPointLogic.getTrafficSource(oauthTokenHeader);
                          };
                          return (
                            hasEditManualExpansesPermission ? (
                              <RouterLink
                                data-tracker="424"
                                style={{ textDecoration: "underline", color: "#9C27B0" }} 
                                to={`/finance/providers_expenses/manual_expenses/manual_expense_details?${queryString}`}
                                onClick={handleLinkClick}>
                                <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                  {row?.expense_name?.length > 50 ? row.expense_name.slice(0, 50) + "..." : row?.expense_name}
                                </div>
                              </RouterLink>
                            ) : (
                              row?.expense_name?.length > 50 ? row.expense_name.slice(0, 50) + "..." : row?.expense_name
                            )
                          );
                        })()}
                      </TableCell>
                      <TableCell align="left">{row?.website_name}</TableCell>
                      <TableCell align="left">{row?.traffic_source__shared_uid ? allTrafficSources.find((source: any) => source.shared_uid === row.traffic_source__shared_uid)?.traffic_source_name || "" : ""}</TableCell>
                      <TableCell>{formatDateToDDMMYYYY(row.date)}</TableCell>
                      <TableCell align="left">{formatNumberWithCommas(row.clicks)}</TableCell>
                      <TableCell align="left">{formatNumberWithCommas(row.impressions)}</TableCell>
                      <TableCell align="left">{formatNumberWithCommas(row.expenses)}</TableCell>
                      <TableCell align="left">{formatDate(row.created)}</TableCell>
                      <TableCell align="left">
                        <div style={{ display: "flex", alignItems: "center", gap: 5, }}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            {row?.created_by && row?.created_by.charAt(0)}
                          </Avatar>
                          {row?.created_by ? row?.created_by : "N/A"}
                        </div>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={filteredData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
              {
                display: "none",
              },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredData?.length / rowsPerPage)}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      </TableContainer>
    </>
  )
}

export default ManualExpenseTable
